import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import {
  Phone, Mail, Youtube, Facebook, Smartphone, Sparkles, Menu, X,
  Hand, Wind, Zap, Heart, Brain, Globe, CheckCircle2, ArrowRight, Play, Quote
} from 'lucide-react';
import { mockData, brand } from '../mock';
import { Reveal, Overline } from '../components/Reveal';
import { RegistrationForm } from '../components/RegistrationForm';

const ANIMATION_DELAY_SHORT = 220;
const ANIMATION_DELAY_MEDIUM = 280;
const ANIMATION_DELAY_LONG = 360;

const outcomeIcons = { Personal: Globe, Internal: Brain, External: Heart };
const whatIcons = [Hand, Wind, Zap];

const Home = () => {
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    document.title = 'Bhagavadgita Healing | 5-Day Frequency Healing Programme with Kriyayogi Bhargousarma';
  }, []);

  const scrollToSection = (id) => {
    setMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const navLinks = [
    { id: 'about', label: 'About' },
    { id: 'healing', label: 'The Method' },
    { id: 'outcomes', label: 'Outcomes' },
    { id: 'program', label: 'Programme' },
    { id: 'gallery', label: 'Videos' },
    { id: 'testimonials', label: 'Voices' },
    { id: 'contact', label: 'Register' }
  ];

  return (
    <div className="min-h-screen bg-[#2A0A18] text-[#F8F6F0] overflow-x-hidden" data-testid="home-page">
      {/* ===== Header ===== */}
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-2xl bg-[#2A0A18]/70 border-b border-[#D4AF37]/10">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <nav className="flex items-center justify-between h-16 md:h-20">
            <button onClick={() => scrollToSection('hero')} className="flex items-center gap-3" data-testid="logo-btn">
              <img src={brand.logo} alt="Chidhguru Gita logo" className="w-10 h-10 md:w-12 md:h-12 rounded-full object-cover" />
              <span className="font-heading text-xl md:text-2xl font-semibold gold-text hidden sm:block">Bhagavadgita Healing</span>
            </button>

            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <button
                  key={link.id}
                  onClick={() => scrollToSection(link.id)}
                  className="text-sm text-[#E6CFC2] hover:text-[#D4AF37] transition-colors tracking-wide"
                  data-testid={`nav-${link.id}`}
                >
                  {link.label}
                </button>
              ))}
              <Button
                onClick={() => scrollToSection('contact')}
                className="bg-gradient-to-r from-[#D4AF37] to-[#B5952F] text-[#2A0A18] font-semibold rounded-none px-6 uppercase tracking-widest text-xs hover:opacity-90 transition-opacity"
                data-testid="header-register-btn"
              >
                Register
              </Button>
            </div>

            <button className="lg:hidden text-[#D4AF37]" onClick={() => setMenuOpen(!menuOpen)} data-testid="mobile-menu-toggle">
              {menuOpen ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
            </button>
          </nav>
        </div>
        {menuOpen && (
          <div className="lg:hidden border-t border-[#D4AF37]/10 bg-[#2A0A18]/95 backdrop-blur-2xl px-5 py-4 flex flex-col gap-4" data-testid="mobile-menu">
            {navLinks.map((link) => (
              <button key={link.id} onClick={() => scrollToSection(link.id)} className="text-left text-[#E6CFC2] hover:text-[#D4AF37] transition-colors">
                {link.label}
              </button>
            ))}
          </div>
        )}
      </header>

      {/* ===== Hero ===== */}
      <section id="hero" className="relative min-h-screen flex items-center justify-center pt-24 pb-16">
        <div className="absolute inset-0">
          <img src={brand.heroBanner} alt="Bhagavadgita Healing cosmic energy" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#2A0A18]/85 via-[#2A0A18]/60 to-[#2A0A18]" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#2A0A18]/80 via-transparent to-[#2A0A18]/80" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(42,10,24,0.85)_0%,rgba(42,10,24,0.45)_45%,transparent_75%)]" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <Reveal>
            <img src={brand.logo} alt="logo" className="w-20 h-20 md:w-24 md:h-24 rounded-full mx-auto mb-6 animate-float shadow-[0_0_40px_rgba(212,175,55,0.35)]" />
          </Reveal>
          <Reveal delay={100}>
            <Overline>Frequency-Based Divine Healing</Overline>
          </Reveal>
          <Reveal delay={150}>
            <h1 className="hero-title font-heading text-6xl sm:text-7xl lg:text-8xl tracking-tight leading-[0.95] mb-4">
              Bhagavadgita Healing
            </h1>
          </Reveal>
          <Reveal delay={ANIMATION_DELAY_SHORT}>
            <p className="font-heading text-3xl md:text-4xl font-semibold text-[#F5D06A] mb-6 drop-shadow-[0_3px_12px_rgba(0,0,0,0.7)]">భగవద్గీత హీలింగ్</p>
          </Reveal>
          <Reveal delay={ANIMATION_DELAY_MEDIUM}>
            <p className="text-base md:text-xl text-[#F8F6F0] font-medium max-w-2xl mx-auto leading-relaxed mb-10 drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]">
              {mockData.hero.tagline}
            </p>
          </Reveal>

          <Reveal delay={ANIMATION_DELAY_LONG}>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => scrollToSection('contact')}
                className="bg-gradient-to-r from-[#D4AF37] to-[#B5952F] text-[#2A0A18] font-bold px-8 py-6 rounded-none uppercase tracking-widest text-sm hover:scale-[1.02] transition-transform animate-gold-pulse"
                data-testid="hero-register-btn"
              >
                Register Now <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                onClick={() => scrollToSection('healing')}
                variant="outline"
                className="bg-transparent text-[#F5D06A] border border-[#D4AF37]/60 hover:border-[#D4AF37] hover:bg-transparent px-8 py-6 rounded-none uppercase tracking-widest text-sm font-semibold"
                data-testid="hero-learn-btn"
              >
                Learn the Method
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===== What is Bhagavadgita Healing ===== */}
      <section id="healing" className="py-24 md:py-32 px-6 relative">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-start">
          <Reveal className="lg:sticky lg:top-32">
            <Overline>The Method</Overline>
            <h2 className="font-heading text-4xl md:text-5xl font-light text-[#D4AF37] leading-tight mb-6">
              {mockData.whatIs.title}
            </h2>
            <p className="text-[#E6CFC2] text-lg leading-relaxed mb-6">{mockData.whatIs.lead}</p>
            <div className="border-l-2 border-[#D4AF37]/40 pl-6">
              <p className="font-heading text-2xl text-[#F8F6F0] italic leading-snug">{mockData.whatIs.closing}</p>
            </div>
          </Reveal>

          <div className="space-y-6">
            {mockData.whatIs.points.map((point, i) => {
              const Icon = whatIcons[i] || Sparkles;
              return (
                <Reveal key={point.title} delay={i * 120}>
                  <div className="bg-[#3A1226] border border-[#D4AF37]/15 p-8 hover:border-[#D4AF37]/40 transition-colors group">
                    <div className="flex items-start gap-5">
                      <div className="w-14 h-14 flex items-center justify-center border border-[#D4AF37]/30 group-hover:bg-[#D4AF37]/10 transition-colors flex-shrink-0">
                        <Icon className="w-6 h-6 text-[#D4AF37]" />
                      </div>
                      <div>
                        <h3 className="font-heading text-2xl text-[#F8F6F0] mb-2">{point.title}</h3>
                        <p className="text-[#E6CFC2] leading-relaxed">{point.description}</p>
                      </div>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== 18 Outcomes ===== */}
      <section id="outcomes" className="py-24 md:py-32 px-6 bg-[#34101F] border-y border-[#D4AF37]/10">
        <div className="max-w-7xl mx-auto">
          <Reveal className="text-center mb-16">
            <Overline>What You Will Solve</Overline>
            <h2 className="font-heading text-4xl md:text-5xl font-light gold-text mb-4">{mockData.outcomes.title}</h2>
            <p className="text-[#E6CFC2] text-lg">{mockData.outcomes.subtitle}</p>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-6">
            {mockData.outcomes.groups.map((group, i) => {
              const Icon = outcomeIcons[group.name] || Sparkles;
              return (
                <Reveal key={group.name} delay={i * 120}>
                  <div className="bg-[#3A1226] border border-[#D4AF37]/15 p-8 h-full hover:border-[#D4AF37]/40 transition-colors">
                    <div className="flex items-center gap-4 mb-2">
                      <Icon className="w-7 h-7 text-[#D4AF37]" />
                      <h3 className="font-heading text-3xl text-[#F8F6F0]">{group.name}</h3>
                    </div>
                    <p className="text-sm text-[#B89A8B] mb-6">{group.description}</p>
                    <ul className="space-y-3">
                      {group.items.map((item) => (
                        <li key={item} className="flex items-start gap-3 text-[#E6CFC2]">
                          <span className="text-[#D4AF37] mt-1.5 w-1.5 h-1.5 rounded-full bg-[#D4AF37] flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== Programme ===== */}
      <section id="program" className="py-24 md:py-32 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <Reveal>
            <Overline>Online Programme</Overline>
            <h2 className="font-heading text-4xl md:text-5xl font-light text-[#D4AF37] mb-4">{mockData.program.title}</h2>
            <p className="text-[#E6CFC2] text-lg mb-8">{mockData.program.subtitle}</p>
          </Reveal>
          <Reveal delay={120}>
            <div className="bg-[#3A1226] border border-[#D4AF37]/15 p-8 md:p-12">
              <p className="text-[#E6CFC2] text-lg leading-relaxed mb-8">{mockData.program.note}</p>
              <Button
                onClick={() => scrollToSection('contact')}
                className="bg-gradient-to-r from-[#D4AF37] to-[#B5952F] text-[#2A0A18] font-bold px-8 py-6 rounded-none uppercase tracking-widest text-sm hover:scale-[1.02] transition-transform"
                data-testid="program-register-btn"
              >
                Register Now <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===== Instructor / About ===== */}
      <section id="about" className="py-24 md:py-32 px-6 bg-[#34101F] border-y border-[#D4AF37]/10">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="relative">
              <div className="absolute -inset-3 border border-[#D4AF37]/20" />
              <img src={brand.instructorPortrait} alt="Kriyayogi Bhargousarma" className="relative w-full object-cover" />
            </div>
          </Reveal>
          <Reveal delay={120}>
            <Overline>Your Guide</Overline>
            <h2 className="font-heading text-4xl md:text-5xl font-light text-[#F8F6F0] mb-2">{mockData.instructor.name}</h2>
            <p className="text-[#D4AF37] tracking-wide mb-6">{mockData.instructor.role}</p>
            <p className="text-[#E6CFC2] text-lg leading-relaxed mb-8">{mockData.instructor.intro}</p>

            <div className="grid grid-cols-3 gap-4 mb-8">
              {mockData.instructor.credentials.map((c) => (
                <div key={c.label} className="border border-[#D4AF37]/20 p-4 text-center">
                  <div className="font-heading text-3xl gold-text">{c.value}</div>
                  <div className="text-[11px] text-[#B89A8B] mt-2 leading-tight">{c.label}</div>
                </div>
              ))}
            </div>

            <div className="space-y-3">
              {mockData.instructor.promises.map((p) => (
                <div key={p} className="flex items-center gap-3 text-[#F8F6F0]">
                  <CheckCircle2 className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                  <span>{p}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===== Videos ===== */}
      <section id="gallery" className="py-24 md:py-32 px-6">
        <div className="max-w-6xl mx-auto">
          <Reveal className="text-center mb-16">
            <Overline>Discover</Overline>
            <h2 className="font-heading text-4xl md:text-5xl font-light text-[#D4AF37] mb-4">{mockData.gallery.title}</h2>
            <p className="text-[#E6CFC2] text-lg">{mockData.gallery.subtitle}</p>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-8">
            {mockData.gallery.videos.map((video, i) => {
              const videoId = video.videoUrl.split('/embed/')[1];
              const thumb = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
              return (
                <Reveal key={video.videoUrl} delay={i * 120}>
                  <div
                    onClick={() => window.open(`https://www.youtube.com/watch?v=${videoId}`, '_blank')}
                    className="group cursor-pointer border border-[#D4AF37]/15 hover:border-[#D4AF37]/40 transition-all shadow-[0_0_30px_rgba(212,175,55,0.06)] hover:shadow-[0_0_40px_rgba(212,175,55,0.18)]"
                    data-testid={`video-card-${i}`}
                  >
                    <div className="relative aspect-video overflow-hidden">
                      <img src={thumb} alt={video.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                      <div className="absolute inset-0 bg-[#2A0A18]/40 group-hover:bg-[#2A0A18]/20 transition-colors flex items-center justify-center">
                        <div className="w-16 h-16 rounded-full bg-[#D4AF37] flex items-center justify-center group-hover:scale-110 transition-transform">
                          <Play className="w-7 h-7 text-[#2A0A18] ml-1" fill="currentColor" />
                        </div>
                      </div>
                    </div>
                    <div className="p-6">
                      <h3 className="font-heading text-xl text-[#F8F6F0]">{video.title}</h3>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== Testimonials ===== */}
      <section id="testimonials" className="py-24 md:py-32 px-6 bg-[#34101F] border-y border-[#D4AF37]/10">
        <div className="max-w-6xl mx-auto">
          <Reveal className="text-center mb-16">
            <Overline>Blessings Received</Overline>
            <h2 className="font-heading text-4xl md:text-5xl font-light gold-text mb-4">{mockData.testimonials.title}</h2>
            <p className="text-[#E6CFC2] text-lg">{mockData.testimonials.subtitle}</p>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {mockData.testimonials.items.map((t, i) => (
              <Reveal key={t.name} delay={(i % 3) * 120}>
                <div className="relative bg-[#2A0A18] border border-[#D4AF37]/15 p-8 h-full flex flex-col hover:border-[#D4AF37]/40 transition-colors" data-testid={`testimonial-${i}`}>
                  <Quote className="w-9 h-9 text-[#D4AF37]/50 mb-4" fill="currentColor" />
                  <p className="text-[#E6CFC2] leading-relaxed flex-grow mb-6">{t.quote}</p>
                  <div className="flex items-center gap-4 pt-4 border-t border-[#D4AF37]/10">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#B5952F] flex items-center justify-center flex-shrink-0">
                      <span className="font-heading text-xl font-bold text-[#2A0A18]">{t.name.charAt(0)}</span>
                    </div>
                    <div>
                      <p className="font-heading text-lg text-[#F8F6F0] leading-tight">{t.name}</p>
                      <p className="text-xs tracking-widest uppercase text-[#B89A8B]">{t.role}</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Registration ===== */}
      <section id="contact" className="py-24 md:py-32 px-6 bg-[#34101F] border-y border-[#D4AF37]/10">
        <div className="max-w-xl mx-auto">
          <Reveal className="text-center mb-10">
            <Overline>Register</Overline>
            <h2 className="font-heading text-4xl md:text-5xl font-light gold-text mb-4">{mockData.contact.title}</h2>
            <p className="text-[#E6CFC2] text-lg leading-relaxed">{mockData.contact.description}</p>
          </Reveal>

          {/* Form */}
          <Reveal delay={120}>
            <RegistrationForm />
          </Reveal>
        </div>
      </section>

      {/* ===== Footer ===== */}
      <footer className="py-20 px-6 bg-[#2A0A18]">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-12 mb-12">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img src={brand.logo} alt="logo" className="w-12 h-12 rounded-full" />
                <span className="font-heading text-xl gold-text">Bhagavadgita Healing</span>
              </div>
              <p className="text-[#B89A8B] leading-relaxed">Transforming lives through the eternal frequency of the Bhagavad Gita.</p>
            </div>
            <div>
              <h4 className="text-xs tracking-[0.3em] uppercase text-[#D4AF37] mb-5">Explore</h4>
              <div className="space-y-3">
                {navLinks.map((link) => (
                  <button key={link.id} onClick={() => scrollToSection(link.id)} className="block text-[#E6CFC2] hover:text-[#D4AF37] transition-colors">
                    {link.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <h4 className="text-xs tracking-[0.3em] uppercase text-[#D4AF37] mb-5">Connect</h4>
              <div className="space-y-3">
                {mockData.contact.phone.map((p) => (
                  <a key={p} href={`tel:${p}`} className="flex items-center gap-3 text-[#E6CFC2] hover:text-[#D4AF37] transition-colors">
                    <Phone className="w-4 h-4" /> +91 {p}
                  </a>
                ))}
                <a href={`mailto:${mockData.contact.email}`} className="flex items-center gap-3 text-[#E6CFC2] hover:text-[#D4AF37] transition-colors break-all">
                  <Mail className="w-4 h-4 flex-shrink-0" /> {mockData.contact.email}
                </a>
                <div className="flex gap-4 pt-3">
                  <a href="https://youtu.be/cyHng3Sz0EA?si=1Hk52TRDJWsrrLDr" target="_blank" rel="noopener noreferrer" className="text-[#E6CFC2] hover:text-[#D4AF37] transition-colors"><Youtube className="w-5 h-5" /></a>
                  <a href="https://www.facebook.com/Kundaliniyogam/" target="_blank" rel="noopener noreferrer" className="text-[#E6CFC2] hover:text-[#D4AF37] transition-colors"><Facebook className="w-5 h-5" /></a>
                  <a href="https://on-app.in/app/br/sreee?oCode=wclds" target="_blank" rel="noopener noreferrer" className="text-[#E6CFC2] hover:text-[#D4AF37] transition-colors"><Smartphone className="w-5 h-5" /></a>
                </div>
              </div>
            </div>
          </div>
          <div className="border-t border-[#D4AF37]/10 pt-8 text-center text-[#B89A8B] text-sm">
            <p>© 2026 Bhagavadgita Healing · Kriyayogi Bhargousarma. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* ===== Floating WhatsApp ===== */}
      <a
        href="https://wa.me/916301066498?text=Namaste%20Gurujii%2C%20I%20would%20like%20to%20know%20more%20about%20the%20Bhagavadgita%20Healing%20programme."
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat on WhatsApp"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 md:w-16 md:h-16 rounded-full bg-[#25D366] flex items-center justify-center shadow-[0_8px_30px_rgba(37,211,102,0.5)] hover:scale-110 transition-transform animate-float"
        data-testid="whatsapp-float-btn"
      >
        <svg viewBox="0 0 32 32" className="w-8 h-8 md:w-9 md:h-9" fill="#FFFFFF" aria-hidden="true">
          <path d="M16.004 3.2c-7.06 0-12.8 5.74-12.8 12.8 0 2.257.594 4.454 1.72 6.394L3.2 28.8l6.6-1.73a12.74 12.74 0 006.2 1.58h.005c7.06 0 12.8-5.74 12.8-12.8s-5.74-12.65-12.8-12.65zm0 23.02h-.004a10.6 10.6 0 01-5.4-1.48l-.388-.23-4.017 1.053 1.073-3.917-.253-.402a10.56 10.56 0 01-1.62-5.646c0-5.86 4.77-10.63 10.64-10.63 2.84 0 5.51 1.107 7.52 3.117a10.56 10.56 0 013.116 7.52c-.001 5.86-4.771 10.63-10.63 10.63zm5.83-7.96c-.32-.16-1.89-.933-2.183-1.04-.293-.107-.507-.16-.72.16-.213.32-.826 1.04-1.013 1.253-.187.213-.373.24-.693.08-.32-.16-1.35-.498-2.57-1.586-.95-.848-1.592-1.895-1.779-2.215-.187-.32-.02-.493.14-.653.144-.143.32-.373.48-.56.16-.187.213-.32.32-.533.107-.213.053-.4-.027-.56-.08-.16-.72-1.735-.986-2.375-.26-.623-.523-.539-.72-.549l-.613-.011c-.213 0-.56.08-.853.4-.293.32-1.12 1.093-1.12 2.667 0 1.573 1.147 3.093 1.307 3.307.16.213 2.257 3.447 5.467 4.833.764.33 1.36.527 1.825.674.767.244 1.464.21 2.016.127.615-.092 1.89-.773 2.156-1.52.267-.747.267-1.387.187-1.52-.08-.133-.293-.213-.613-.373z"/>
        </svg>
      </a>
    </div>
  );
};

export default Home;
